import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

interface BillingDetailViewProps {
  open: boolean;
  onClose: () => void;
  type: 'estimate' | 'invoice' | 'payment';
  data: any;
}

export const BillingDetailView: React.FC<BillingDetailViewProps> = ({
  open,
  onClose,
  type,
  data,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: string) => {
    const statusConfig: any = {
      'Paid': { color: 'success', variant: 'filled' },
      'Pending': { color: 'warning', variant: 'outlined' },
      'Overdue': { color: 'error', variant: 'filled' },
      'Sent': { color: 'info', variant: 'outlined' },
      'Draft': { color: 'default', variant: 'outlined' },
      'Completed': { color: 'success', variant: 'filled' },
      'Estimate': { color: 'info', variant: 'outlined' },
      'Approved': { color: 'success', variant: 'outlined' },
    };
    
    const config = statusConfig[status] || { color: 'default', variant: 'outlined' };
    
    return (
      <Chip
        label={status}
        color={config.color as any}
        variant={config.variant as any}
        size="small"
      />
    );
  };

  const getTitle = () => {
    switch (type) {
      case 'estimate':
        return `Estimate ${data.number}`;
      case 'invoice':
        return `Invoice ${data.number}`;
      case 'payment':
        return `Payment ${data.reference}`;
      default:
        return 'Details';
    }
  };

  const renderEstimateDetails = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Estimate Date
          </Typography>
          <Typography variant="body1">{formatDate(data.date)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box sx={{ mt: 0.5 }}>{getStatusChip(data.status)}</Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body1">{data.description}</Typography>
        </Grid>
      </Grid>

      {data.items && data.items.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Line Items
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2">Subtotal:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body2">{formatCurrency(data.subtotal || data.amount)}</Typography>
          </Grid>
          {data.tax > 0 && (
            <>
              <Grid item xs={6}>
                <Typography variant="body2">Tax:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2">{formatCurrency(data.tax)}</Typography>
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Total:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(data.total || data.amount)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const renderInvoiceDetails = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Invoice Date
          </Typography>
          <Typography variant="body1">{formatDate(data.date)}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Due Date
          </Typography>
          <Typography variant="body1">{formatDate(data.dueDate)}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box sx={{ mt: 0.5 }}>{getStatusChip(data.status)}</Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body1">{data.description}</Typography>
        </Grid>
      </Grid>

      {data.items && data.items.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Line Items
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2">Subtotal:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body2">{formatCurrency(data.subtotal || data.amount)}</Typography>
          </Grid>
          {data.tax > 0 && (
            <>
              <Grid item xs={6}>
                <Typography variant="body2">Tax:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2">{formatCurrency(data.tax)}</Typography>
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Total:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(data.total || data.amount)}
            </Typography>
          </Grid>
          {data.paid !== undefined && data.paid > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="success.main">Paid:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="success.main">
                  {formatCurrency(data.paid)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>Balance Due:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {formatCurrency(data.amount - data.paid)}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </>
  );

  const renderPaymentDetails = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Payment Date
          </Typography>
          <Typography variant="body1">{formatDate(data.date)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box sx={{ mt: 0.5 }}>{getStatusChip(data.status)}</Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Payment Method
          </Typography>
          <Typography variant="body1">{data.method}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Reference Number
          </Typography>
          <Typography variant="body1">{data.reference}</Typography>
        </Grid>
        {data.invoice && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Invoice
            </Typography>
            <Typography variant="body1">{data.invoice}</Typography>
          </Grid>
        )}
        {data.notes && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Notes
            </Typography>
            <Typography variant="body1">{data.notes}</Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="h6">Amount Paid:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="h6" color="success.main">
              {formatCurrency(data.amount)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );

  const renderContent = () => {
    switch (type) {
      case 'estimate':
        return renderEstimateDetails();
      case 'invoice':
        return renderInvoiceDetails();
      case 'payment':
        return renderPaymentDetails();
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{getTitle()}</Typography>
          <Box>
            {type !== 'payment' && (
              <>
                <IconButton size="small" sx={{ mr: 1 }} title="Download PDF">
                  <DownloadIcon />
                </IconButton>
                <IconButton size="small" sx={{ mr: 1 }} title="Print">
                  <PrintIcon />
                </IconButton>
                <IconButton size="small" sx={{ mr: 1 }} title="Email">
                  <EmailIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};